import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import rocketChatModel, { RocketChatState } from '@/core/models/rocket-chat.model'

import { RootState } from '@/redux/rootReducer'

export const selectChatChannelByConsultation = (state: RootState, consultationId: string): string | undefined => state.rocketChat.channels.byConsultation[consultationId] || undefined
export const selectChatGroupByConsultation = (state: RootState, consultationId: string): string | undefined => state.rocketChat.groups.byConsultation[consultationId] || undefined
export const selectChatChannelByEstablishment = (state: RootState, establishmentId: string): string | undefined => state.rocketChat.channels.byEstablishment[establishmentId] || undefined
export const selectChatDirectMessageByStaff = (state: RootState, staffId: string): string | undefined => state.rocketChat.directMessages.byStaff[staffId] || undefined

const initialState: RocketChatState = rocketChatModel.getInitialState()

const rocketChatSlice = createSlice({
  name: rocketChatModel.reducerName,
  initialState,
  reducers: {
    login(state, action: PayloadAction<HTMLIFrameElement>) { },
    createConsultationChatGroup: {
      reducer: (state, action: PayloadAction<{ consultationId: string, groupName: string, members: string[], isEmbeddedLayout?: boolean }>) => {
        state.requestTracker.create.loading = true
      },
      prepare: (consultationId: string, groupName: string, members: string[], isEmbeddedLayout?: boolean) => ({
        payload: { groupName, consultationId, members, isEmbeddedLayout }
      })
    },
    createConsultationChatGroupSuccess(state, action: PayloadAction<{ consultationId: string, iframeUrl: string }>) {
      const { consultationId, iframeUrl } = action.payload
      state.groups.byConsultation[consultationId] = iframeUrl
      state.requestTracker.create.error = null
      state.requestTracker.create.loading = false
    },
    createConsultationChatGroupFailure(state, action) {
      state.requestTracker.create.error = action.payload
      state.requestTracker.create.loading = false
    },
    getConsultationChatGroup: {
      reducer: (state, action: PayloadAction<{ consultationId: string, groupName: string, isEmbeddedLayout?: boolean }>) => {
        state.requestTracker.get.loading = true
      },
      prepare: (consultationId: string, groupName: string, isEmbeddedLayout?: boolean) => ({
        payload: { groupName, consultationId, isEmbeddedLayout }
      })
    },
    getConsultationChatGroupSuccess(state, action: PayloadAction<{ consultationId: string, iframeUrl: string }>) {
      const { consultationId, iframeUrl } = action.payload
      state.groups.byConsultation[consultationId] = iframeUrl
      state.requestTracker.get.error = null
      state.requestTracker.get.loading = false
    },
    getConsultationChatGroupFailure(state, action) {
      state.requestTracker.get.error = action.payload
      state.requestTracker.get.loading = false
    },
    createEstablishmentChatChannel: {
      reducer: (state, action: PayloadAction<{ channelName: string; establishmentId: string, isEmbeddedLayout?: boolean }>) => {
        state.requestTracker.create.loading = true
      },
      prepare: (channelName: string, establishmentId: string, isEmbeddedLayout?: boolean) => ({
        payload: { channelName, establishmentId, isEmbeddedLayout },
      }),
    },
    createEstablishmentChatChannelSuccess(state, action: PayloadAction<{ establishmentId: string; iframeUrl: string }>) {
      const { establishmentId, iframeUrl } = action.payload
      state.channels.byEstablishment[establishmentId] = iframeUrl
      state.requestTracker.create.error = null
      state.requestTracker.create.loading = false
    },
    createEstablishmentChatChannelFailure(state, action) {
      state.requestTracker.create.error = action.payload
      state.requestTracker.create.loading = false
    },
    createStaffChatDirectMessage: {
      reducer: (state, action: PayloadAction<{ staffId: string, username: string, isEmbeddedLayout?: boolean }>) => {
        state.requestTracker.create.loading = true
      },
      prepare: (staffId: string, username: string, isEmbeddedLayout?: boolean) => ({
        payload: { staffId, username, isEmbeddedLayout }
      })
    },
    createStaffChatDirectMessageSuccess(state, action: PayloadAction<{ staffId: string, iframeUrl: string }>) {
      const { staffId, iframeUrl } = action.payload
      state.directMessages.byStaff[staffId] = iframeUrl
      state.requestTracker.create.error = null
      state.requestTracker.create.loading = false
    },
    createStaffChatDirectMessageFailure(state, action) {
      state.requestTracker.create.error = action.payload
      state.requestTracker.create.loading = false
    },
  },
})

export const {
  login,
  createConsultationChatGroup,
  createConsultationChatGroupSuccess,
  createConsultationChatGroupFailure,
  getConsultationChatGroup,
  getConsultationChatGroupSuccess,
  getConsultationChatGroupFailure,
  createEstablishmentChatChannel,
  createEstablishmentChatChannelSuccess,
  createEstablishmentChatChannelFailure,
  createStaffChatDirectMessage,
  createStaffChatDirectMessageSuccess,
  createStaffChatDirectMessageFailure,
} = rocketChatSlice.actions

export default rocketChatSlice.reducer
